import {
  getApp as getAppMobile,
  getApps as getAppsMobile,
  initializeApp as initializeAppMobile,
  ReactNativeFirebase
} from '@react-native-firebase/app';
import { getAuth as getNativeAuth } from '@react-native-firebase/auth';
import { getFirestore as getNativeFirestore } from '@react-native-firebase/firestore';
import { getMessaging as getNativeMessaging } from '@react-native-firebase/messaging';
import { getStorage as getNativeStorage } from '@react-native-firebase/storage';
import { getAnalytics, isSupported as isAnalyticsSupported } from 'firebase/analytics';
import {
  FirebaseApp as WebFirebaseApp,
  getApp as getWebApp,
  getApps as getWebApps,
  initializeApp as initializeWebApp
} from 'firebase/app';
import { getAuth as getWebAuth } from 'firebase/auth';
import { getFirestore as getWebFirestore } from 'firebase/firestore';
import { getMessaging as getWebMessaging, isSupported as isMessagingSupported } from 'firebase/messaging';
import { getStorage as getWebStorage } from 'firebase/storage';
import { Platform } from 'react-native';

import { ENV } from '@/constants/config';

const firebaseConfig = {
  apiKey: process.env.EXPO_PUBLIC_FB_API_KEY ?? '',
  authDomain: process.env.EXPO_PUBLIC_FB_AUTH_DOMAIN ?? '',
  projectId: process.env.EXPO_PUBLIC_FB_PROJECT_ID ?? '',
  storageBucket: process.env.EXPO_PUBLIC_FB_STORAGE_BUCKET ?? '',
  messagingSenderId: process.env.EXPO_PUBLIC_FB_MESSAGING_SENDER_ID ?? '',
  appId: process.env.EXPO_PUBLIC_FB_APP_ID ?? '',
  measurementId: process.env.EXPO_PUBLIC_FB_MEASUREMENT_ID ?? ''
};

// Separate app instances
const webApp: WebFirebaseApp =
  Platform.OS === 'web'
    ? getWebApps().length
      ? getWebApp()
      : initializeWebApp(firebaseConfig)
    : ({} as WebFirebaseApp); // Dummy object for native to satisfy types

const nativeAppInstance =
  Platform.OS !== 'web'
    ? getAppsMobile().length
      ? getAppMobile()
      : initializeAppMobile(firebaseConfig)
    : ({} as ReactNativeFirebase.FirebaseApp);

// Cached instances
let _auth: ReturnType<typeof getWebAuth> | ReturnType<typeof getNativeAuth> | undefined;
let _db: ReturnType<typeof getWebFirestore> | ReturnType<typeof getNativeFirestore> | undefined;
let _storage: ReturnType<typeof getWebStorage> | ReturnType<typeof getNativeStorage> | undefined;
let _messaging: ReturnType<typeof getWebMessaging> | ReturnType<typeof getNativeMessaging> | undefined;

export const auth = () => {
  if (_auth) return _auth;
  if (Platform.OS === 'web') {
    _auth = getWebAuth(webApp as WebFirebaseApp);
    _auth.useDeviceLanguage();
  } else {
    _auth = getNativeAuth(nativeAppInstance as ReactNativeFirebase.FirebaseApp);
  }
  return _auth;
};

export const db = () => {
  if (_db) return _db;
  if (Platform.OS === 'web') {
    _db = getWebFirestore(webApp as WebFirebaseApp, ENV === 'production' ? 'bs-firestore-prod' : 'bs-firestore-dev');
  } else {
    _db = getNativeFirestore(
      nativeAppInstance as ReactNativeFirebase.FirebaseApp,
      ENV === 'production' ? 'bs-firestore-prod' : 'bs-firestore-dev'
    );
  }
  return _db;
};

export const storage = () => {
  if (_storage) return _storage;
  if (Platform.OS === 'web') {
    _storage = getWebStorage(webApp as WebFirebaseApp);
  } else {
    _storage = getNativeStorage(nativeAppInstance as ReactNativeFirebase.FirebaseApp);
  }
  return _storage;
};

export const messaging = async () => {
  if (_messaging) return _messaging;
  if (Platform.OS === 'web') {
    if (await isMessagingSupported()) {
      _messaging = getWebMessaging(webApp as WebFirebaseApp);
      return _messaging;
    }
    return undefined;
  } else {
    _messaging = getNativeMessaging(nativeAppInstance as ReactNativeFirebase.FirebaseApp);
    return _messaging;
  }
};

export const initWebAnalytics = async () => {
  if (Platform.OS === 'web' && (await isAnalyticsSupported())) {
    getAnalytics(webApp);
  }
};
